import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { compressImage } from '../lib/imageCompression';
import { usePricing } from '../hooks/usePricing';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import {
  FileCheck, Upload, Sparkles, Plus, X, FileText, Trash2, ExternalLink,
} from 'lucide-react';

// Credential document types — must mirror the backend's accepted `doc_types` values exactly.
const DOCUMENT_TYPES = [
  'tesda_cert', 'license', 'diploma', 'resume', 'proof_of_work',
  'nbi_clearance', 'police_clearance', 'business_permit', 'other',
];
const MAX_RAW_BYTES = 10 * 1024 * 1024; // 10MB pre-compression (matches backend contract ≤10MB)
const MAX_DOC_ROWS = 30; // matches backend _MAX_QUALIFICATION_DOCS
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];

const makeRow = (id) => ({ id, type: '', file: null });

/**
 * Dashboard "My Credentials" section. A Pro attaches their OWN credential documents (diploma,
 * TESDA certificate, license, clearance, portfolio, …) to their profile. These are NOT verified
 * by the Platform — the Platform verifies IDENTITY only (the "Verified" badge = profiles.verified,
 * a separate flow). The credential doc TYPES are teased publicly on the Pro's listings; the
 * DOCUMENTS themselves are shared with a client only when they reveal the Pro's contact.
 *
 * No admin review, no status, no government ID. Just upload + manage (list / delete).
 * Self-hides when pricing.services_enabled is false (renders null).
 */
const ProCredentialsSection = ({ profile }) => {
  const { t } = useTranslation();
  const { pricing } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);
  const rowFileRefs = useRef(new Map());
  const rowIdRef = useRef(1);

  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docRows, setDocRows] = useState(() => [makeRow(0)]);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchMine = useCallback(async () => {
    if (!profile?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { setLoading(false); return; }
      const res = await api.get('/api/pro-credentials/mine', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setCredentials(res.data?.credentials || []);
    } catch (error) {
      console.error('[ProCredentialsSection] fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    // Flag-off (incl. while pricing resolves): leave loading untouched — the flag-off render
    // path returns null; flipping loading here caused a brief wrong-state flash on flag flip.
    if (servicesEnabled) {
      fetchMine();
    }
  }, [servicesEnabled, fetchMine]);

  const readyRows = docRows.filter((r) => r.file && r.type);
  const canSubmit = !submitting && readyRows.length > 0;

  const validateFile = (file) => {
    const fileName = file.name?.toLowerCase() || '';
    const hasValidExtension = /\.(jpe?g|png|webp|pdf)$/i.test(fileName);
    if (!ALLOWED_TYPES.includes(file.type) && !(file.type === '' && hasValidExtension)) {
      toast.error(t('services.errorImageType'));
      return false;
    }
    if (file.size > MAX_RAW_BYTES) {
      toast.error(t('services.verifErrorFileSize'));
      return false;
    }
    return true;
  };

  const addRow = () => {
    if (docRows.length >= MAX_DOC_ROWS) return;
    const id = rowIdRef.current;
    rowIdRef.current += 1;
    setDocRows((rows) => [...rows, makeRow(id)]);
  };

  const removeRow = (rowId) => {
    rowFileRefs.current.delete(rowId);
    setDocRows((rows) => rows.filter((r) => r.id !== rowId));
  };

  const setRowType = (rowId, type) => {
    setDocRows((rows) => rows.map((r) => (r.id === rowId ? { ...r, type } : r)));
  };

  const handleRowFileSelect = (rowId, e) => {
    const file = (e.target.files || [])[0];
    const input = rowFileRefs.current.get(rowId);
    if (input) input.value = '';
    if (!file) return;
    if (!validateFile(file)) return;
    setDocRows((rows) => rows.map((r) => (r.id === rowId ? { ...r, file } : r)));
  };

  const resetForm = () => {
    const id = rowIdRef.current;
    rowIdRef.current += 1;
    rowFileRefs.current.clear();
    setDocRows([makeRow(id)]);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const rows = docRows.filter((r) => r.file && r.type);
    if (rows.length === 0) {
      toast.error(t('services.verifErrorNeedDocument'));
      return;
    }
    // Block if any row is half-filled (file without a type, or a type without a file) so the user
    // isn't told "saved" while an incomplete row is silently dropped.
    const partialRow = docRows.some((r) => (r.file && !r.type) || (!r.file && r.type));
    if (partialRow) {
      toast.error(t('services.credErrorIncompleteRow'));
      return;
    }
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error(t('services.loginRequired'));
        return;
      }
      const form = new FormData();
      // Parallel `files` / `doc_types` arrays, same order.
      for (const row of rows) {
        let uploadFile = row.file;
        try {
          uploadFile = await compressImage(row.file, 'document');
        } catch {
          uploadFile = row.file;
        }
        form.append('files', uploadFile, uploadFile.name || 'document.jpg');
        form.append('doc_types', row.type);
      }
      await api.post('/api/pro-credentials', form, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        timeout: UPLOAD_TIMEOUT,
      });
      toast.success(t('services.credSubmitSuccess'));
      resetForm();
      await fetchMine();
    } catch (err) {
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      toast.error(detail || t('services.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (credentialId) => {
    if (deletingId) return;
    setDeletingId(credentialId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error(t('services.loginRequired'));
        return;
      }
      await api.delete(`/api/pro-credentials/${credentialId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setCredentials((list) => list.filter((c) => c.id !== credentialId));
      toast.success(t('services.credDeleteSuccess'));
    } catch (err) {
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      toast.error(detail || t('services.errorGeneric'));
    } finally {
      setDeletingId(null);
    }
  };

  // Flag off → render nothing.
  if (!servicesEnabled) return null;

  if (loading) {
    return (
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-4">{t('services.credTitle')}</h2>
        <Card><Skeleton className="h-32" /></Card>
      </div>
    );
  }

  // No credentials yet → collapse to heading + blurb + Add button (expand on click) so an optional
  // feature does not read like a required onboarding task on every dashboard.
  const collapsed = credentials.length === 0 && !expanded;

  return (
    <div data-testid="pro-credentials-section">
      <div className="mb-4">
        <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
          <FileCheck size={22} className="text-emerald-600" aria-hidden="true" />
          {t('services.credTitle')}
        </h2>
      </div>

      {collapsed ? (
        <Card className="p-4 space-y-3">
          <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <Sparkles size={16} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
            <div>
              <p className="font-medium">{t('services.credWhyTitle')}</p>
              <p className="text-emerald-800 mt-0.5">{t('services.credWhyBody')}</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setExpanded(true)}>
            <Plus size={16} className="mr-2" aria-hidden="true" />
            {t('services.credAddTitle')}
          </Button>
        </Card>
      ) : (
      <Card className="p-4 space-y-4">
        {/* Why add credentials? blurb */}
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <Sparkles size={16} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
          <div>
            <p className="font-medium">{t('services.credWhyTitle')}</p>
            <p className="text-emerald-800 mt-0.5">{t('services.credWhyBody')}</p>
          </div>
        </div>

        {/* Existing credentials */}
        {credentials.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('services.credYourDocuments')}</p>
            {credentials.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {t(`services.verifDocType_${c.doc_type}`, c.doc_type)}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({c.is_pdf ? 'PDF' : t('services.credentialImage')})
                      </span>
                    </p>
                    {c.created_at && (
                      <p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {c.doc_url && (
                    <a
                      href={c.doc_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={t('services.credViewDoc')}
                    >
                      <ExternalLink size={16} aria-hidden="true" />
                    </a>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-red-600"
                    disabled={deletingId === c.id}
                    aria-label={t('services.credDelete')}
                    onClick={() => handleDelete(c.id)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upload form */}
        <div className="space-y-4 pt-2 border-t border-dashed">
          <div>
            <p className="text-sm font-medium">{t('services.credAddTitle')}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('services.credUploadHint')}</p>
          </div>

          {docRows.map((row) => (
            <div key={row.id} className="rounded-lg border border-border p-3">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 space-y-2">
                  <Select
                    value={row.type}
                    onValueChange={(val) => setRowType(row.id, val)}
                    disabled={submitting}
                  >
                    <SelectTrigger aria-label={t('services.verifDocTypeLabel')}>
                      <SelectValue placeholder={t('services.verifDocTypePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_TYPES.map((dt) => (
                        <SelectItem key={dt} value={dt}>{t(`services.verifDocType_${dt}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input
                    ref={(el) => {
                      if (el) rowFileRefs.current.set(row.id, el);
                      else rowFileRefs.current.delete(row.id);
                    }}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                    onChange={(e) => handleRowFileSelect(row.id, e)}
                    className="hidden"
                  />
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={submitting}
                      onClick={() => rowFileRefs.current.get(row.id)?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                      {row.file ? t('services.verifChangeFile') : t('services.verifChooseFile')}
                    </Button>
                    {row.file && (
                      <span className="text-sm text-muted-foreground min-w-0 flex-1 truncate" title={row.file.name}>
                        {row.file.name}
                      </span>
                    )}
                  </div>
                </div>
                {docRows.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-muted-foreground hover:text-red-600"
                    disabled={submitting}
                    aria-label={t('services.verifRemoveDocument')}
                    onClick={() => removeRow(row.id)}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                )}
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={submitting || docRows.length >= MAX_DOC_ROWS}
            onClick={addRow}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('services.verifAddDocument')}
          </Button>

          <p className="text-xs text-muted-foreground">{t('services.credNotVerifiedNote')}</p>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full"
          >
            {submitting ? t('services.credSubmitting') : t('services.credSubmit')}
          </Button>
        </div>
      </Card>
      )}
    </div>
  );
};

export default ProCredentialsSection;
